import { useState } from 'react'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'

/**
 * Division information interface
 */
interface Division {
  schedule_id: string
}

/**
 * Coupon information interface
 */
interface CouponInfo {
  id: string
  code: string
  discount: number
  type: 'percentage' | 'amount'
  subtotal: number
  discount_amount: number
  max_uses: number
}

/**
 * Props interface for the usePromoCode hook
 */
interface UsePromoCodeProps {
  division: Division
  isTeamRep: boolean
  promoCode: string
  setCouponInfo: (coupon: CouponInfo | null) => void
}

/**
 * 🔄 usePromoCode: Custom hook for promo code validation
 * Handles API calls for coupon validation and manages error states
 */
export const usePromoCode = ({
  division,
  isTeamRep,
  promoCode,
  setCouponInfo,
}: UsePromoCodeProps) => {
  console.log('🔄 usePromoCode: Initializing promo code hook')

  const [promoCodeError, setPromoCodeError] = useState<string | null>(null)
  const [isPromoCodeValid, setIsPromoCodeValid] = useState(false)

  /**
   * 🔄 validateCoupon: Validates the promo code with the API
   */
  const validateCoupon = async () => {
    console.log('🔄 validateCoupon: Validating promo code with API')

    try {
      setPromoCodeError(null)
      setIsPromoCodeValid(false)

      const res = await req(
        `/registration/${division?.schedule_id}/coupon/verify`,
        {
          method: 'POST',
          body: JSON.stringify({
            code: promoCode,
            registration_type_id: isTeamRep ? 1 : 2,
          }),
        }
      )

      setCouponInfo({
        discount: res?.data?.discount,
        type: res?.data?.discount_type,
        id: res?.data?.id,
        code: res?.data?.code,
        subtotal: res?.data?.subtotal,
        discount_amount: res?.data?.discount_amount,
        max_uses: res?.data?.max_uses,
      })

      setIsPromoCodeValid(true)
      console.log('✅ validateCoupon: Promo code validated successfully')
    } catch (e) {
      console.error('❌ validateCoupon: Error validating promo code', e)
      setPromoCodeError(getErrorMessage(e))
    }
  }

  return {
    validateCoupon,
    promoCodeError,
    isPromoCodeValid,
  }
}
